'use client';

import { Button, DialogFooter } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Spinner } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { StatusNotification } from '../../../../../../StatusNotification/StatusNotification';
import { useKycDialog } from '../../../context/KycDialogContext';
import { FileDropZone } from '../components/FileDropZone';
import { FileList } from '../components/FileList';
import type { UploadStepProps, UploadedFile } from '../types';
import { formatFileSize } from '../utils/formatFileSize';
import { ACCEPTED_FILE_TYPES, MAX_FILES, MAX_FILE_SIZE_MB, validateFile } from '../utils/validateFile';

/**
 * Upload step component with its own form element for proper Enter key handling.
 * This demonstrates the pattern of having individual forms within a multistep flow.
 *
 * Each step manages its own form submission and validation, then calls the
 * provided onSubmit callback to coordinate with the parent component.
 */
function UploadStep({ onSubmit, isUploading }: UploadStepProps) {
  const t = useTranslations('form');
  const step = useTranslations('global_dialogs.kyc_verification.manual_upload');
  const { isNameChangeFlow } = useKycDialog();
  const { isUseNameChangeManualUploadCopy } = getSiteConfig().PROFILE_INFO;
  const isNameChangeCopy = isNameChangeFlow && isUseNameChangeManualUploadCopy;
  const copyPrefix = isNameChangeCopy ? 'name_change.' : '';

  const translateStep = useCallback((key: string, values?: Record<string, string | number>) => step(`${copyPrefix}${key}` as 'accepted_formats', values), [copyPrefix, step]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [validationError, setValidationError] = useState<string>('');

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const newFiles: UploadedFile[] = files.map(file => ({
        file,
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        sizeFormatted: formatFileSize(file.size),
      }));

      const filesWithErrors: UploadedFile[] = [];

      const validatedFiles = newFiles.filter(fileData => {
        const validation = validateFile(fileData.file);

        if (!validation.isValid && validation.error) {
          filesWithErrors.push({
            ...fileData,
            error: step(validation.error),
          });
          return false;
        }

        return validation.isValid;
      });

      setUploadedFiles(prev => [...prev, ...validatedFiles, ...filesWithErrors].slice(0, MAX_FILES));
      setValidationError('');
    },
    [step]
  );

  const handleRemoveFile = useCallback((id: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id));
    setValidationError('');
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validFiles = uploadedFiles.filter(file => !file.error);
    // Validate before submission - check if there are no valid files
    if (validFiles.length === 0) {
      setValidationError(step('validation.no_files'));
      return;
    }

    // Clear any previous validation errors
    setValidationError('');

    const files = validFiles.map(fileData => fileData.file);
    onSubmit(files);
  };

  const isDropZoneDisabled = uploadedFiles.length >= MAX_FILES || isUploading;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 pt-10 md:pt-6" data-testid="upload-step">
      <div className="flex flex-col gap-6 md:gap-10 flex-1 min-h-0 overflow-hidden">
        <div className="shrink-0">
          <FileDropZone onFilesSelected={handleFilesSelected} disabled={isDropZoneDisabled} currentFileCount={uploadedFiles.length} />
        </div>

        {validationError && (
          <div className="shrink-0" data-testid="validation-error">
            <StatusNotification type="error" description={validationError} />
          </div>
        )}

        <div className={twMerge('flex flex-col gap-4 min-w-0', uploadedFiles.length > 0 && 'min-h-0 flex-1')} data-testid="upload-step-files-section">
          {uploadedFiles.length > 0 && <FileList files={uploadedFiles} onRemove={handleRemoveFile} disabled={isUploading} />}

          <ul className="flex flex-col gap-2 list-none shrink-0">
            <li className="flex items-start gap-2">
              <span className="text-primary btn-label flex-shrink-0">•</span>
              <p className="paragraph-xs text-muted-foreground">{translateStep('accepted_formats', { formats: ACCEPTED_FILE_TYPES.join(', ') })}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary btn-label flex-shrink-0">•</span>
              <p className="paragraph-xs text-muted-foreground">{translateStep('max_file_size', { size: MAX_FILE_SIZE_MB })}</p>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary btn-label flex-shrink-0">•</span>
              <p className="paragraph-xs text-muted-foreground">{translateStep('max_files', { count: MAX_FILES })}</p>
            </li>
          </ul>
        </div>
      </div>
      <DialogFooter data-track-container="Actions">
        <Button type="submit" variant="primary" disabled={isUploading} className="w-full sm:w-auto" data-testid="submit-button" data-track-name="Continue">
          {isUploading ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : translateStep('submit_button')}
        </Button>
      </DialogFooter>
    </form>
  );
}

export { UploadStep };
